import { Injectable, inject } from '@angular/core';
import { GeoJSONSource, LngLatBounds, Map } from 'maplibre-gl';
import * as martinez from 'martinez-polygon-clipping';
import { UtilsService } from '../utils/utils.service';
import { MapPaletteService } from './map-palette.service';
import { Range } from 'src/app/models/range';

// Color constants
const INNER_POLYGON_COLOR = '#3CE6A7';
const OUTER_POLYGON_COLOR = '#27DB99';

// Animation constants
const MORPH_POINTS = 128;
/** The placeholder is a wobbly circle — 64 points is visually identical and halves its tessellation cost. */
const PULSE_POINTS = 64;
const FALLBACK_RADIUS_KM = 30;
const PULSE_PERIOD = 2800;
const MORPH_DURATION = 900;
/**
 * Animation tick interval (~30fps). Every tick re-tessellates both polygons and
 * re-uploads their buffers, and rAF runs at the display rate — 120/144Hz
 * monitors were paying 2⁠–⁠2.5× the work for a 2.8s breathe where 30fps is
 * indistinguishable.
 */
const ANIM_TICK_MS = 33;

/**
 * The placeholder is a circle, the real range follows roads — a straight-line
 * circle of the same nominal range always overshoots the polygon, so the
 * estimate is shortened by this factor. Deliberately a mild undershoot: the
 * morph then reveals the range outwards instead of collapsing inwards.
 */
const CROW_FLIES_FACTOR = 0.75;
/**
 * Shared by both fits so the placeholder and the final range are framed alike.
 * A fraction of the shorter viewport side, not a fixed pixel amount: the old
 * 200px margin over-zoomed-out on desktop and ate most of a phone-sized
 * viewport, so the blob is padded just enough to barely fit instead.
 */
const FIT_PADDING_FRACTION = 0.1;

/**
 * How a drawn range (or its placeholder) frames the map: an animated flight
 * from the current view, an instant jump (the startup overview — the page
 * should open on the range, not fly out to it), or no camera change at all
 * (a background refresh while following the car).
 */
export type RangeFit = 'animate' | 'instant' | 'none';

@Injectable({
  providedIn: 'root',
})
export class MapRangeDrawerService {

  private mapInstance: Map | null = null;
  private loadingAnimationId: number | null = null;
  private morphAnimationId: number | null = null;
  private loadingCenter: [number, number] | null = null;
  private currentAnimCoordsOuter: GeoJSON.Position[] | null = null;
  private currentAnimCoordsInner: GeoJSON.Position[] | null = null;
  private loadingRadiusKm: number = FALLBACK_RADIUS_KM;
  /** The range currently on screen, kept so a style reload can restore it. */
  private lastRange: Range | null = null;
  /**
   * Live breathing opacities. Carried as feature properties in the setData the
   * animation already sends (read by ['get', ...] paint expressions) instead of
   * per-frame setPaintProperty calls — each of those ran a style update pass,
   * and there were four per frame.
   */
  private animOpacity = { outerFill: 0.15, outerLine: 0.6, innerFill: 0.12, innerLine: 0.6 };

  private readonly utilsService = inject(UtilsService);
  private readonly mapPaletteService = inject(MapPaletteService);

  /** Widget palette: the range follows color_accent1, the inner blob a lighter tone of it. */
  private get innerColor(): string { return this.mapPaletteService.accentLight(INNER_POLYGON_COLOR, 0.25); }
  private get outerColor(): string { return this.mapPaletteService.accent(OUTER_POLYGON_COLOR); }

  public setMapInstance(map: Map): void {
    this.mapInstance = map;
  }

  // ── Loading: show a placeholder range on the map ──

  public startLoadingAnimation(centerLngLat: [number, number], estimatedRangeKm?: number, fit: RangeFit = 'animate'): void {
    this.stopLoadingAnimation();
    this.clearRange();
    this.loadingCenter = centerLngLat;
    this.loadingRadiusKm = estimatedRangeKm && estimatedRangeKm > 0 ? estimatedRangeKm : FALLBACK_RADIUS_KM;

    if (!this.mapInstance) return;

    const outerRadius = this.loadingRadiusKm * CROW_FLIES_FACTOR;
    const innerRadius = outerRadius * 0.65;

    const outerCircle = this.createCirclePolygon(centerLngLat, outerRadius, PULSE_POINTS);
    const innerCircle = this.createCirclePolygon(centerLngLat, innerRadius, PULSE_POINTS);

    this.animOpacity = { outerFill: 0.15, outerLine: 0.6, innerFill: 0.12, innerLine: 0.6 };

    // Opacities are read from feature properties so the breathing rides along
    // the geometry setData instead of costing extra setPaintProperty passes.
    const fillOpacityExpr = ['get', 'fillOpacity'] as any;
    const lineOpacityExpr = ['get', 'lineOpacity'] as any;

    // Outer donut (ring between outer and inner)
    this.mapInstance.addSource('range-anim-outer', {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: { fillOpacity: this.animOpacity.outerFill, lineOpacity: this.animOpacity.outerLine },
        geometry: { type: 'Polygon', coordinates: [outerCircle, innerCircle] }
      }
    });
    this.mapInstance.addLayer({
      id: 'range-anim-outer-fill', type: 'fill', source: 'range-anim-outer',
      paint: { 'fill-color': this.outerColor, 'fill-opacity': fillOpacityExpr }
    });
    this.mapInstance.addLayer({
      id: 'range-anim-outer-border', type: 'line', source: 'range-anim-outer',
      paint: { 'line-color': this.outerColor, 'line-width': 2, 'line-opacity': lineOpacityExpr }
    });

    // Inner polygon
    this.mapInstance.addSource('range-anim-inner', {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: { fillOpacity: this.animOpacity.innerFill, lineOpacity: this.animOpacity.innerLine },
        geometry: { type: 'Polygon', coordinates: [innerCircle] }
      }
    });
    this.mapInstance.addLayer({
      id: 'range-anim-inner-fill', type: 'fill', source: 'range-anim-inner',
      paint: { 'fill-color': this.innerColor, 'fill-opacity': fillOpacityExpr }
    });
    this.mapInstance.addLayer({
      id: 'range-anim-inner-border', type: 'line', source: 'range-anim-inner',
      paint: { 'line-color': this.innerColor, 'line-width': 2, 'line-opacity': lineOpacityExpr }
    });

    this.keepLayerOnTop('cs-groups-layer');
    this.keepLayerOnTop('cs-layer');
    this.keepLayerOnTop('cs-fav-layer');
    this.keepLayerOnTop('cursor-layer');

    // Fit map to show the entire loading animation
    if (fit !== 'none') {
      const bounds = new LngLatBounds();
      outerCircle.forEach(coord => bounds.extend(coord as [number, number]));
      this.fitBoundsToViewport(bounds, fit === 'animate');
    }

    const startTime = performance.now();
    const center = centerLngLat;
    let lastTick = 0;

    const animate = (now: number) => {
      if (!this.mapInstance || !this.loadingCenter) return;

      if (now - lastTick < ANIM_TICK_MS) {
        this.loadingAnimationId = requestAnimationFrame(animate);
        return;
      }
      lastTick = now;

      const t = ((now - startTime) % PULSE_PERIOD) / PULSE_PERIOD;
      const breathe = 0.5 - 0.5 * Math.cos(t * 2 * Math.PI);

      // Gentle radius breathing (±8%)
      const outerR = outerRadius * (0.92 + 0.16 * breathe);
      const innerR = innerRadius * (0.92 + 0.16 * breathe);

      // Organic wobble shapes
      const outerCoords = this.createOrganicShape(center, outerR, PULSE_POINTS, now);
      const innerCoords = this.createOrganicShape(center, innerR, PULSE_POINTS, now + 5000);
      this.currentAnimCoordsOuter = outerCoords;
      this.currentAnimCoordsInner = innerCoords;

      // Reverse inner ring winding for donut hole
      const innerReversed = [...innerCoords].reverse();

      // Opacity breathing, carried as feature properties
      this.animOpacity = {
        outerFill: 0.12 + 0.12 * breathe,
        outerLine: 0.4 + 0.4 * breathe,
        innerFill: 0.08 + 0.1 * breathe,
        innerLine: 0.4 + 0.4 * breathe,
      };

      const outerSource = this.mapInstance.getSource('range-anim-outer') as GeoJSONSource;
      outerSource?.setData({
        type: 'Feature',
        properties: { fillOpacity: this.animOpacity.outerFill, lineOpacity: this.animOpacity.outerLine },
        geometry: { type: 'Polygon', coordinates: [outerCoords, innerReversed] }
      });

      const innerSource = this.mapInstance.getSource('range-anim-inner') as GeoJSONSource;
      innerSource?.setData({
        type: 'Feature',
        properties: { fillOpacity: this.animOpacity.innerFill, lineOpacity: this.animOpacity.innerLine },
        geometry: { type: 'Polygon', coordinates: [innerCoords] }
      });

      this.loadingAnimationId = requestAnimationFrame(animate);
    };

    this.loadingAnimationId = requestAnimationFrame(animate);
  }

  public stopLoadingAnimation(): void {
    if (this.loadingAnimationId != null) {
      cancelAnimationFrame(this.loadingAnimationId);
      this.loadingAnimationId = null;
    }
    if (this.morphAnimationId != null) {
      cancelAnimationFrame(this.morphAnimationId);
      this.morphAnimationId = null;
    }
    this.clearAnimLayers();
    this.loadingCenter = null;
    this.currentAnimCoordsOuter = null;
    this.currentAnimCoordsInner = null;
  }

  private clearAnimLayers(): void {
    ['range-anim-outer-fill', 'range-anim-outer-border', 'range-anim-inner-fill', 'range-anim-inner-border'].forEach(id => {
      if (this.mapInstance?.getLayer(id)) this.mapInstance!.removeLayer(id);
    });
    ['range-anim-outer', 'range-anim-inner'].forEach(id => {
      if (this.mapInstance?.getSource(id)) this.mapInstance!.removeSource(id);
    });
  }

  // ── Draw range: morph from placeholder if active ──

  /**
   * Draw the final range and frame the map on it as `fit` says — through the
   * morph when a placeholder is up, directly otherwise (the placeholder is
   * skipped when the map was not loaded yet at calculation start, and the
   * startup overview must still frame the range). With 'none' the blob is
   * swapped in place and the camera is left alone (background GPS refresh).
   */
  public drawRange(range: Range, fit: RangeFit = 'animate'): void {
    const wasLoading = this.loadingCenter != null;

    // Stop pulse loop but keep anim layers for morph
    if (this.loadingAnimationId != null) {
      cancelAnimationFrame(this.loadingAnimationId);
      this.loadingAnimationId = null;
    }
    if (this.morphAnimationId != null) {
      cancelAnimationFrame(this.morphAnimationId);
      this.morphAnimationId = null;
    }

    this.clearRange();
    this.lastRange = range;

    const range1Converted = this.utilsService.closeAndConvert(range.Range1);
    const range2Converted = this.utilsService.closeAndConvert(range.Range2);

    if (wasLoading && this.currentAnimCoordsOuter && this.mapInstance?.getSource('range-anim-outer')) {
      try {
        this.morphToFinalRange(range1Converted, range2Converted, range, fit);
      } catch {
        // Fallback: if morph fails, just show final range immediately
        this.clearAnimLayers();
        this.loadingCenter = null;
        this.currentAnimCoordsOuter = null;
        this.currentAnimCoordsInner = null;
        this.drawFinalLayers(range1Converted, range2Converted, range);
      }
    } else {
      this.clearAnimLayers();
      this.loadingCenter = null;
      this.currentAnimCoordsOuter = null;
      this.currentAnimCoordsInner = null;
      this.drawFinalLayers(range1Converted, range2Converted, range);
      this.fitMapToRange(range1Converted, range2Converted, fit);
    }
  }

  private morphToFinalRange(
    range1Converted: GeoJSON.Position[][],
    range2Converted: GeoJSON.Position[][],
    range: Range,
    fit: RangeFit
  ): void {
    // Find the largest rings from Range1 (outer) and Range2 (inner) as morph targets
    const targetOuter = this.findLargestRing(range1Converted);
    const targetInner = this.findLargestRing(range2Converted);
    if (!targetOuter) {
      this.clearAnimLayers();
      this.loadingCenter = null;
      this.currentAnimCoordsOuter = null;
      this.currentAnimCoordsInner = null;
      this.drawFinalLayers(range1Converted, range2Converted, range);
      this.fitMapToRange(range1Converted, range2Converted, fit);
      return;
    }

    // Use the stored current animation coordinates
    const currentOuter = this.currentAnimCoordsOuter!;
    const currentInner = this.currentAnimCoordsInner || currentOuter;

    // Resample both outer rings
    const srcOuter = this.resampleRing(currentOuter, MORPH_POINTS);
    const tgtOuter = this.alignToSource(srcOuter, this.resampleRing(targetOuter, MORPH_POINTS));

    // Resample both inner rings
    const srcInner = this.resampleRing(currentInner, MORPH_POINTS);
    const tgtInner = targetInner
      ? this.alignToSource(srcInner, this.resampleRing(targetInner, MORPH_POINTS))
      : tgtOuter; // fallback: inner morphs to same as outer if no Range2

    // Start fit-bounds early so map zooms while morphing
    this.fitMapToRange(range1Converted, range2Converted, fit);

    // Capture current opacity from the breathing animation as morph start values
    const startOp = { ...this.animOpacity };

    // Final target opacities (matching zoom=11 values of the zoom-dependent expressions)
    const endOuterFillOp = 0.5;
    const endInnerFillOp = 0.3;

    const startTime = performance.now();
    let lastTick = 0;

    const animateStep = (now: number) => {
      if (!this.mapInstance) return;

      const elapsed = now - startTime;
      const rawT = Math.min(elapsed / MORPH_DURATION, 1);

      // Same ~30fps cap as the pulse; the final frame always runs.
      if (rawT < 1 && now - lastTick < ANIM_TICK_MS) {
        this.morphAnimationId = requestAnimationFrame(animateStep);
        return;
      }
      lastTick = now;

      // Ease-out quart for smooth deceleration
      const t = 1 - Math.pow(1 - rawT, 4);

      // Interpolate outer coordinates
      const morphedOuter: GeoJSON.Position[] = [];
      for (let i = 0; i < MORPH_POINTS; i++) {
        morphedOuter.push([
          srcOuter[i][0] + t * (tgtOuter[i][0] - srcOuter[i][0]),
          srcOuter[i][1] + t * (tgtOuter[i][1] - srcOuter[i][1])
        ]);
      }
      morphedOuter.push([...morphedOuter[0]]);

      // Interpolate inner coordinates
      const morphedInner: GeoJSON.Position[] = [];
      for (let i = 0; i < MORPH_POINTS; i++) {
        morphedInner.push([
          srcInner[i][0] + t * (tgtInner[i][0] - srcInner[i][0]),
          srcInner[i][1] + t * (tgtInner[i][1] - srcInner[i][1])
        ]);
      }
      morphedInner.push([...morphedInner[0]]);

      // Reverse inner ring for donut hole in outer polygon
      const innerReversed = [...morphedInner].reverse();

      // Interpolate opacity from the breathing state to the final values,
      // carried as feature properties like in the pulse loop
      const outerSource = this.mapInstance.getSource('range-anim-outer') as GeoJSONSource;
      outerSource?.setData({
        type: 'Feature',
        properties: {
          fillOpacity: startOp.outerFill + t * (endOuterFillOp - startOp.outerFill),
          lineOpacity: startOp.outerLine + t * (1 - startOp.outerLine)
        },
        geometry: { type: 'Polygon', coordinates: [morphedOuter, innerReversed] }
      });

      const innerSource = this.mapInstance.getSource('range-anim-inner') as GeoJSONSource;
      innerSource?.setData({
        type: 'Feature',
        properties: {
          fillOpacity: startOp.innerFill + t * (endInnerFillOp - startOp.innerFill),
          lineOpacity: startOp.innerLine + t * (1 - startOp.innerLine)
        },
        geometry: { type: 'Polygon', coordinates: [morphedInner] }
      });

      if (rawT < 1) {
        this.morphAnimationId = requestAnimationFrame(animateStep);
      } else {
        // Morph complete → update the existing anim layers in-place with final data
        // No layer swap: the anim layers become the permanent layers.
        // This runs inside a rAF callback, outside drawRange's try/catch — an
        // exception here used to leave the placeholder stuck with the real
        // range never drawn, so fall back to plainly drawn final layers.
        try {
          this.applyFinalDataToAnimLayers(range1Converted, range2Converted, range);
        } catch {
          this.clearAnimLayers();
          this.drawFinalLayers(range1Converted, range2Converted, range);
        }
        this.loadingCenter = null;
        this.currentAnimCoordsOuter = null;
        this.currentAnimCoordsInner = null;
      }
    };

    this.morphAnimationId = requestAnimationFrame(animateStep);
  }

  private buildFinalGeoJSON(
    range1Converted: GeoJSON.Position[][],
    range2Converted: GeoJSON.Position[][],
    range: Range
  ): { innerData: GeoJSON.FeatureCollection; outerData: GeoJSON.FeatureCollection } {
    const innerFeatures: GeoJSON.Feature[] = range2Converted.map(ring => ({
      type: 'Feature' as const, properties: {},
      geometry: { type: 'Polygon' as const, coordinates: [ring] as GeoJSON.Position[][] }
    }));

    const outerFeatures: GeoJSON.Feature[] = [];
    let xor: any[] | null = null;
    if (range.Range1.length > 0 && range.Range2.length > 0) {
      try {
        xor = (martinez as any).xor([range1Converted], [range2Converted]);
      } catch {
        // martinez throws on degenerate/self-touching rings that road-network
        // polygons occasionally produce. Fall through to un-clipped Range1
        // below: the overlap gets double-tinted, but the range still draws
        // instead of silently vanishing.
        xor = null;
      }
    }
    if (xor) {
      xor.forEach((p: any) => {
        outerFeatures.push({
          type: 'Feature', properties: {},
          geometry: { type: 'Polygon', coordinates: p as GeoJSON.Position[][] }
        });
      });
    } else if (range.Range1.length > 0) {
      range1Converted.forEach(ring => {
        outerFeatures.push({
          type: 'Feature', properties: {},
          geometry: { type: 'Polygon', coordinates: [ring] as GeoJSON.Position[][] }
        });
      });
    }

    return {
      innerData: { type: 'FeatureCollection', features: innerFeatures },
      outerData: { type: 'FeatureCollection', features: outerFeatures },
    };
  }

  private applyFinalDataToAnimLayers(
    range1Converted: GeoJSON.Position[][],
    range2Converted: GeoJSON.Position[][],
    range: Range
  ): void {
    const { innerData, outerData } = this.buildFinalGeoJSON(range1Converted, range2Converted, range);
    const innerFillOpacity = ['interpolate', ['linear'], ['zoom'], 11, 0.3, 16, 0] as any;
    const outerFillOpacity = ['interpolate', ['linear'], ['zoom'], 11, 0.5, 16, 0] as any;

    // Update existing anim sources with final geometry
    const outerSource = this.mapInstance?.getSource('range-anim-outer') as GeoJSONSource;
    outerSource?.setData(outerData);

    const innerSource = this.mapInstance?.getSource('range-anim-inner') as GeoJSONSource;
    innerSource?.setData(innerData);

    // Set final zoom-dependent paint properties
    this.mapInstance?.setPaintProperty('range-anim-outer-fill', 'fill-opacity', outerFillOpacity);
    this.mapInstance?.setPaintProperty('range-anim-outer-border', 'line-opacity', 1);
    this.mapInstance?.setPaintProperty('range-anim-inner-fill', 'fill-opacity', innerFillOpacity);
    this.mapInstance?.setPaintProperty('range-anim-inner-border', 'line-opacity', 1);

    this.keepLayerOnTop('cs-groups-layer');
    this.keepLayerOnTop('cs-layer');
    this.keepLayerOnTop('cs-fav-layer');
    this.keepLayerOnTop('cursor-layer');
  }

  private drawFinalLayers(
    range1Converted: GeoJSON.Position[][],
    range2Converted: GeoJSON.Position[][],
    range: Range
  ): void {
    const { innerData, outerData } = this.buildFinalGeoJSON(range1Converted, range2Converted, range);
    const innerFillOpacity = ['interpolate', ['linear'], ['zoom'], 11, 0.3, 16, 0] as any;
    const outerFillOpacity = ['interpolate', ['linear'], ['zoom'], 11, 0.5, 16, 0] as any;

    if (innerData.features.length > 0) {
      this.mapInstance?.addSource('range-inner', {
        type: 'geojson',
        data: innerData
      });
      this.mapInstance?.addLayer({
        id: 'range-inner-fill', type: 'fill', source: 'range-inner',
        paint: { 'fill-color': this.innerColor, 'fill-opacity': innerFillOpacity }
      });
      this.mapInstance?.addLayer({
        id: 'range-inner-border', type: 'line', source: 'range-inner',
        paint: { 'line-color': this.innerColor, 'line-width': 2 }
      });
    }

    if (outerData.features.length > 0) {
      this.mapInstance?.addSource('range-outer', {
        type: 'geojson',
        data: outerData
      });
      this.mapInstance?.addLayer({
        id: 'range-outer-fill', type: 'fill', source: 'range-outer',
        paint: { 'fill-color': this.outerColor, 'fill-opacity': outerFillOpacity }
      });
      this.mapInstance?.addLayer({
        id: 'range-outer-border', type: 'line', source: 'range-outer',
        paint: { 'line-color': this.outerColor, 'line-width': 2 }
      });
    }

    this.keepLayerOnTop('cs-groups-layer');
    this.keepLayerOnTop('cs-layer');
    this.keepLayerOnTop('cs-fav-layer');
    this.keepLayerOnTop('cursor-layer');
  }

  // ── Geometry helpers ──

  private createOrganicShape(center: [number, number], radiusKm: number, points: number, time: number): GeoJSON.Position[] {
    const coords: GeoJSON.Position[] = [];
    const [lng, lat] = center;
    for (let i = 0; i <= points; i++) {
      const angle = (i / points) * 2 * Math.PI;
      // Organic perturbation: multiple sine waves create a natural-looking boundary
      const wobble = 1
        + 0.06 * Math.sin(angle * 3 + time * 0.0015)
        + 0.04 * Math.sin(angle * 5 - time * 0.001)
        + 0.025 * Math.sin(angle * 7 + time * 0.002);
      const r = radiusKm * wobble;
      const dLat = (r / 111.32) * Math.sin(angle);
      const dLng = (r / (111.32 * Math.cos(lat * Math.PI / 180))) * Math.cos(angle);
      coords.push([lng + dLng, lat + dLat]);
    }
    return coords;
  }
  private createCirclePolygon(center: [number, number], radiusKm: number, points: number = 64): GeoJSON.Position[] {
    const coords: GeoJSON.Position[] = [];
    const [lng, lat] = center;
    for (let i = 0; i <= points; i++) {
      const angle = (i / points) * 2 * Math.PI;
      const dLat = (radiusKm / 111.32) * Math.sin(angle);
      const dLng = (radiusKm / (111.32 * Math.cos(lat * Math.PI / 180))) * Math.cos(angle);
      coords.push([lng + dLng, lat + dLat]);
    }
    return coords;
  }

  private resampleRing(ring: GeoJSON.Position[], count: number): GeoJSON.Position[] {
    // ring: closed ring (first ≈ last)
    const pts = ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
      ? ring.slice(0, -1) : ring.slice();
    const n = pts.length;
    if (n === 0) return [];
    if (n === 1) return Array(count).fill(pts[0]);

    // Build cumulative arc-length (including closing edge back to start)
    const cumLen: number[] = new Array(n + 1);
    cumLen[0] = 0;
    for (let i = 1; i <= n; i++) {
      const prev = pts[i - 1];
      const curr = pts[i % n];
      cumLen[i] = cumLen[i - 1] + Math.hypot(curr[0] - prev[0], curr[1] - prev[1]);
    }
    const totalLen = cumLen[n];
    if (totalLen === 0) return Array(count).fill(pts[0]);

    const result: GeoJSON.Position[] = [];
    let segIdx = 0;

    for (let i = 0; i < count; i++) {
      const d = (i / count) * totalLen;

      while (segIdx < n - 1 && cumLen[segIdx + 1] < d) segIdx++;

      const segStart = cumLen[segIdx];
      const segEnd = cumLen[segIdx + 1];
      const t = segEnd > segStart ? (d - segStart) / (segEnd - segStart) : 0;

      const p0 = pts[segIdx];
      const p1 = pts[(segIdx + 1) % n];

      result.push([
        p0[0] + t * (p1[0] - p0[0]),
        p0[1] + t * (p1[1] - p0[1])
      ]);
    }

    return result;
  }

  private alignToSource(source: GeoJSON.Position[], target: GeoJSON.Position[]): GeoJSON.Position[] {
    // Rotate target array so its starting point is closest to source[0]
    const srcFirst = source[0];
    let minDist = Infinity;
    let bestIdx = 0;

    for (let i = 0; i < target.length; i++) {
      const d = Math.hypot(target[i][0] - srcFirst[0], target[i][1] - srcFirst[1]);
      if (d < minDist) {
        minDist = d;
        bestIdx = i;
      }
    }

    return [...target.slice(bestIdx), ...target.slice(0, bestIdx)];
  }

  private findLargestRing(rings: GeoJSON.Position[][]): GeoJSON.Position[] | null {
    if (rings.length === 0) return null;
    if (rings.length === 1) return rings[0];

    let largest = rings[0];
    let maxArea = this.ringArea(rings[0]);

    for (let i = 1; i < rings.length; i++) {
      const area = this.ringArea(rings[i]);
      if (area > maxArea) {
        maxArea = area;
        largest = rings[i];
      }
    }
    return largest;
  }

  private ringArea(ring: GeoJSON.Position[]): number {
    let area = 0;
    for (let i = 0; i < ring.length - 1; i++) {
      area += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1];
    }
    return Math.abs(area) / 2;
  }

  private fitMapToRange(range1: GeoJSON.Position[][], range2: GeoJSON.Position[][], fit: RangeFit): void {
    if (!this.mapInstance || fit === 'none') return;

    const bounds = new LngLatBounds();
    const extendRings = (rings: GeoJSON.Position[][]) => {
      rings.forEach(ring => ring.forEach(coord => bounds.extend(coord as [number, number])));
    };

    extendRings(range1);
    extendRings(range2);

    this.fitBoundsToViewport(bounds, fit === 'animate');
  }

  /**
   * Zoom the map so the given bounds just barely fit the current viewport,
   * centred, with a small margin. In range view the follow camera also keeps
   * the cursor at the viewport center (see the motion controller's
   * getViewOffset), so this framing and the follow camera agree on the center.
   */
  private fitBoundsToViewport(bounds: LngLatBounds, animate: boolean): void {
    if (!this.mapInstance || bounds.isEmpty()) return;

    const container = this.mapInstance.getContainer();
    const w = container.clientWidth || window.innerWidth;
    const h = container.clientHeight || window.innerHeight;
    const padding = Math.round(Math.min(w, h) * FIT_PADDING_FRACTION);

    // Keep the current rotation: a zoom computed for a north-up frame
    // under-fits a rotated one.
    const bearing = this.mapInstance.getBearing();
    this.mapInstance.fitBounds(bounds, { padding, bearing, animate });
  }

  /**
   * Restore the range layers after a full style reload — setStyle() wipes every
   * source and layer, and the re-init path only redrew routes/stations, so the
   * blob silently vanished on a skin or pointer-type (desktop/mobile) switch.
   * Draws the final layers directly: no loading animation, no re-fit — the
   * camera is already where the user left it.
   */
  public redrawRange(): void {
    if (!this.mapInstance || !this.lastRange) return;
    const range = this.lastRange;
    this.clearRange(); // drops lastRange too — restore it after
    this.lastRange = range;
    this.drawFinalLayers(
      this.utilsService.closeAndConvert(range.Range1),
      this.utilsService.closeAndConvert(range.Range2),
      range
    );
  }

  public clearRange(): void {
    this.lastRange = null;
    const layers = [
      'range-inner-border', 'range-inner-fill', 'range-outer-border', 'range-outer-fill',
      'range-anim-outer-fill', 'range-anim-outer-border', 'range-anim-inner-fill', 'range-anim-inner-border'
    ];
    const sources = ['range-inner', 'range-outer', 'range-anim-outer', 'range-anim-inner'];
    layers.forEach(id => { if (this.mapInstance?.getLayer(id)) this.mapInstance!.removeLayer(id); });
    sources.forEach(id => { if (this.mapInstance?.getSource(id)) this.mapInstance!.removeSource(id); });
  }

  keepLayerOnTop(layerId: string) {
    if (this.mapInstance?.getLayer(layerId)) {
      this.mapInstance?.moveLayer(layerId);
    }
  }
}
